// This Mac's runner's local reads (offline design §3, Poulpe-828), asked of
// the Mac app (mac/Sources/PoulpeMacUI/LocalReads.swift), which runs
// `poulpe-runner local-read` for the page: a page cannot open the runner's
// Unix socket. The page posts `{localRead: {id, document, read, …}}` and the
// shell answers a `poulpe-local-read` event: `{id, text}`, the runner's JSON
// reply (crates/poulpe-protocol/src/local_reads.rs), or `{id}` alone when
// this Mac's runner gives nothing: none is installed, it is older than
// Poulpe-827, its socket is missing, or it took too long. Nothing, a refusal
// and a malformed reply all read as null here: no overlay, and no error
// beyond the app's own "Offline".
import { hasShell, inMacShell, tellShell } from '../lib/shell'
import { itemOf } from './transcript'
import type { CheckProgress, LiveCheck, RunnerJob, TranscriptItem } from './types'
import { uuid4 } from './uuid'

const ANSWER = 'poulpe-local-read'
/** The app kills `local-read` after 5 s; past this, the shell is not answering. */
const PATIENCE = 10_000

export type LocalRead = { read: 'sessions' } | { read: 'checks' } | { read: 'transcript'; session: string; after?: string }

/** A page of a session's transcript, as this Mac's runner holds it: its ids and times are the runner's own. */
export interface LocalPage { items: TranscriptItem[]; more: boolean }

/** A check of this Mac's runner, as its local reads give it. */
export interface LocalCheck { job: string; progress: CheckProgress | null; ended: boolean }

/**
 * The runner installed on this Mac, whose local reads the Mac app gives; null
 * outside the app or when none is installed. The shell names it on <html>
 * (`data-claude-api-key`, Poulpe-718), read again for each document.
 */
export function localRunner(): string | null {
  if (typeof document === 'undefined' || !inMacShell() || !hasShell()) return null
  return document.documentElement.dataset.claudeApiKey || null
}

/** Asks the shell for one read; the runner's reply as an object, or null for nothing. */
export function readLocally(request: LocalRead): Promise<Record<string, unknown> | null> {
  const document = globalThis.document?.documentElement.dataset.sessionDocument
  if (!localRunner() || !document) return Promise.resolve(null)
  return new Promise(resolve => {
    const id = uuid4()
    const done = (value: Record<string, unknown> | null) => {
      window.removeEventListener(ANSWER, listener)
      window.clearTimeout(timeout)
      resolve(value)
    }
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail
      if (typeof detail !== 'object' || detail === null || (detail as { id?: unknown }).id !== id) return
      const text = (detail as { text?: unknown }).text
      if (typeof text !== 'string') return done(null)
      try {
        const reply: unknown = JSON.parse(text)
        done(typeof reply === 'object' && reply !== null && (reply as { reply?: unknown }).reply === request.read ? reply as Record<string, unknown> : null)
      } catch {
        done(null)
      }
    }
    const timeout = window.setTimeout(() => done(null), PATIENCE)
    window.addEventListener(ANSWER, listener)
    tellShell({ localRead: { id, document, ...request } })
  })
}

/** A page of `session`'s transcript after the runner's item `after`, or from its oldest; null for nothing. */
export async function localTranscript(session: string, after: string | null): Promise<LocalPage | null> {
  const reply = await readLocally(after ? { read: 'transcript', session, after } : { read: 'transcript', session })
  if (!reply || !Array.isArray(reply.items)) return null
  const items = reply.items.map(itemOf).filter((item): item is TranscriptItem => item !== null)
  return { items, more: reply.more === true }
}

/** This Mac's runner's checks; null for nothing. */
export async function localChecks(): Promise<LocalCheck[] | null> {
  const reply = await readLocally({ read: 'checks' })
  if (!reply || !Array.isArray(reply.checks)) return null
  return reply.checks.flatMap((value: unknown) => {
    if (typeof value !== 'object' || value === null) return []
    const { job, progress, ended } = value as Record<string, unknown>
    if (typeof job !== 'string' || job === '') return []
    const known = typeof progress === 'object' && progress !== null ? progress as CheckProgress : null
    return [{ job, progress: known, ended: ended === true }]
  })
}

/** The runner's checks as live checks, their project and session from the jobs the device kept. */
export function asLiveChecks(checks: readonly LocalCheck[], jobs: ReadonlyMap<string, RunnerJob>): LiveCheck[] {
  return checks.map(check => {
    const job = jobs.get(check.job)
    return {
      job: check.job, project: job?.project_id ?? '', session: job?.session_id ?? null,
      proposal: null, landing: null, reported_at: null, usual_ms: null, progress: check.progress,
    }
  })
}
